/**
 * FileTransferList — the File destination's composition layer.
 *
 * A "Files" header with a persistent Choose-file action, then an Outgoing
 * section and an Incoming section of `FileTransferCard` rows, or the drop zone
 * when there is nothing at all.
 *
 * Headless: no transport, no storage, no platform API. The host owns the
 * transfer machine and passes resolved rows.
 *
 * ONE outgoing row is the practical maximum today — the engine holds a single
 * active send — but this takes an array rather than an optional single item so
 * that a future send queue is a host change, not a re-shape here. The design's
 * "Outgoing (2)" frame is placeholder data (both cards carry the identical
 * filename and size), not a stated requirement.
 */
import { type ReactNode, useEffect, useId, useRef } from "react";
import { Button } from "../Button";
import { FileDropZone, type FileDropZoneLabels } from "../FileDropZone";
import {
  FileTransferCard,
  type FileTransferCardLabels,
  type FileTransferState,
} from "../FileTransferCard";
import { Text } from "../Text";
import styles from "./FileTransferList.module.css";

export interface FileTransferItem {
  /** Stable across renders — the transferId. */
  id: string;
  fileName: string;
  fileSizeLabel: string;
  state: FileTransferState;
  /** Which route the bytes take; omitted until the transport is negotiated. */
  transport?: "direct" | "relay";
  onCancel?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
}

export interface FileTransferListLabels {
  /** Section title. The nav says "File" (singular); this says "Files". */
  title: string;
  chooseFile: string;
  /** Takes the row count, e.g. "Outgoing (1)". */
  outgoing: (count: number) => string;
  incoming: (count: number) => string;
  /** Only rendered when more than one offer is pending. */
  acceptAll: string;
  dropZone: FileDropZoneLabels;
  card: FileTransferCardLabels;
  /**
   * Overrides `card` for the Incoming section. A few of the card's states read
   * differently depending on which way the bytes went — a finished transfer is
   * "Sent!" outbound but "Received!" inbound — and the card itself cannot
   * choose, because direction is deliberately not one of its props (see its
   * module doc). The list is the lowest layer that knows, so the distinction
   * lives here. Omit to use `card` for both sections.
   */
  incomingCard?: FileTransferCardLabels;
}

export interface FileTransferListProps {
  outgoing: FileTransferItem[];
  incoming: FileTransferItem[];
  onFileSelected: (file: File) => void;
  /** Omit to hide the bulk action even when several offers are pending. */
  onAcceptAll?: () => void;
  labels: FileTransferListLabels;
  /** Disables both pickers — e.g. no paired device to send to. */
  pickerDisabled?: boolean;
  /**
   * Rendered under the header, ABOVE the sections. For conditions that block
   * or precede the whole list — no paired device, a rejected file. Something
   * the user must read before they act.
   */
  banner?: ReactNode;
  /**
   * Rendered BELOW the sections. For notes about work already in flight, which
   * only make sense once you have seen the rows they describe — the
   * keep-this-open warning, peer progress. Above the sections these read as
   * instructions arriving before their subject.
   */
  footnote?: ReactNode;
}

function Section({
  label,
  action,
  items,
  cardLabels,
}: {
  label: string;
  action?: ReactNode;
  items: FileTransferItem[];
  cardLabels: FileTransferCardLabels;
}) {
  // Point the landmark at the visible heading rather than repeating the string
  // in an aria-label, so the two cannot drift apart.
  const headingId = useId();
  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.sectionHead}>
        <Text id={headingId} size="xs" tone="tertiary">
          {label}
        </Text>
        {action}
      </div>
      {/* Not `.list` — that class name carries a `.list > li` flex-column
          override elsewhere in this package which would restack every card. */}
      <ul className={styles.rows}>
        {items.map((item) => (
          <li key={item.id}>
            <FileTransferCard
              fileName={item.fileName}
              fileSizeLabel={item.fileSizeLabel}
              state={item.state}
              transport={item.transport}
              onCancel={item.onCancel}
              onAccept={item.onAccept}
              onReject={item.onReject}
              labels={cardLabels}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

export function FileTransferList({
  outgoing,
  incoming,
  onFileSelected,
  onAcceptAll,
  labels,
  pickerDisabled = false,
  banner,
  footnote,
}: FileTransferListProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const chooseRef = useRef<HTMLButtonElement>(null);
  const empty = outgoing.length === 0 && incoming.length === 0;
  // Bulk-accept only earns its place when there is more than one decision to make.
  const pendingOffers = incoming.filter((i) => i.state.kind === "offered").length;
  const showAcceptAll = Boolean(onAcceptAll) && pendingOffers > 1;

  /**
   * Both the drop zone and Accept-all unmount at the moment they succeed — the
   * zone when the first transfer starts, Accept-all when it drops the pending
   * count below two. A keyboard user standing on either would have focus fall
   * to <body>, losing their place exactly on success. Catch that and hand
   * focus to the header picker, which is present in every state.
   */
  const wasEmpty = useRef(empty);
  const hadAcceptAll = useRef(showAcceptAll);
  useEffect(() => {
    const lostZone = wasEmpty.current && !empty;
    const lostAcceptAll = hadAcceptAll.current && !showAcceptAll;
    wasEmpty.current = empty;
    hadAcceptAll.current = showAcceptAll;
    // Only rescue focus we actually dropped; never steal it from elsewhere.
    if ((lostZone || lostAcceptAll) && document.activeElement === document.body) {
      chooseRef.current?.focus();
    }
  }, [empty, showAcceptAll]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text as="h2" size="m" weight="semibold">
          {labels.title}
        </Text>
        <Button
          ref={chooseRef}
          variant="outline"
          size="s"
          disabled={pickerDisabled}
          onClick={() => inputRef.current?.click()}
        >
          {labels.chooseFile}
        </Button>
        {/* The header picker shares the drop zone's contract but not its DOM —
            the zone is unmounted as soon as anything is in flight, and the
            header action stays available throughout. */}
        <input
          ref={inputRef}
          type="file"
          className={styles.input}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelected(file);
            // Reset so re-picking the same file fires change again.
            e.target.value = "";
          }}
        />
      </div>

      {/* Padded to the same gutter as the header and the rows. Rendered bare it
          sat flush against the panel edge while everything around it was inset,
          which read as a stray sentence rather than a status line. */}
      {banner && <div className={styles.banner}>{banner}</div>}

      <div className={styles.body}>
        {empty ? (
          <FileDropZone
            onFileSelected={onFileSelected}
            labels={labels.dropZone}
            disabled={pickerDisabled}
          />
        ) : (
          <>
            {outgoing.length > 0 && (
              <Section
                label={labels.outgoing(outgoing.length)}
                items={outgoing}
                cardLabels={labels.card}
              />
            )}
            {incoming.length > 0 && (
              <Section
                label={labels.incoming(incoming.length)}
                items={incoming}
                cardLabels={labels.incomingCard ?? labels.card}
                action={
                  showAcceptAll && onAcceptAll ? (
                    <Button variant="ghost" size="xs" onClick={onAcceptAll}>
                      {labels.acceptAll}
                    </Button>
                  ) : undefined
                }
              />
            )}
          </>
        )}
        {footnote && <div className={styles.footnote}>{footnote}</div>}
      </div>
    </div>
  );
}
